import { i18next } from '@yola/ws-sdk';
import { CUSTOM_DOMAIN, PUBLISHING } from '../constants/upsell-types';

const getDefaultCaptions = () => ({
  description: {
    first: i18next.t('Select premium features that you need to build a professional website.'),
    second: i18next.t('Get visitors, sell products, and grow your business online'),
  },
});

const getBuyDomainFreeTrialCaptions = (isCustomDomain) => ({
  backButtonLabel: i18next.t('Back'),
  title: `${i18next.t('Try the best of Yola.')}<br/>${i18next.t('First two weeks are on us')}`,
  description: {
    first: i18next.t('Start the 14-day free trial {payForDomain} today. Cancel anytime', {
      payForDomain: isCustomDomain ? i18next.t('and only pay for your domain') : '',
    }),
  },
  plan: {
    buttonLabel: i18next.t('Start Free Trial'),
  },
});

const getFreeTrialCaptions = () => ({
  backButtonLabel: i18next.t('Back to editor'),
  title: i18next.t('Start Your Free Website Trial'),
  description: {
    first: i18next.t(
      "Choose the website plan that's right for your business. 14-day free trial. Cancel anytime."
    ),
  },
  plan: {
    buttonLabel: i18next.t('Start Free Trial'),
  },
});

const getCaptionsForPaywall = ({ upsellType, upsells = {}, details = {} }) => {
  const defaultCaptions = getDefaultCaptions();
  const freeTrialCaptions = getFreeTrialCaptions();
  const buyDomainFreeTrialCaptions = getBuyDomainFreeTrialCaptions(details.isCustomDomain);

  if (upsellType === CUSTOM_DOMAIN) {
    if (details.isFreeTrialAvailable) {
      return buyDomainFreeTrialCaptions;
    }

    return {
      backButtonLabel: i18next.t('Back'),
      ...defaultCaptions,
    };
  }

  if (upsellType === PUBLISHING) {
    if (details.isFreeTrialUpsellFeatureFlagEnabled) {
      if (details.isFreeTrialAvailable) {
        if (details.isCustomDomain) {
          return {
            ...buyDomainFreeTrialCaptions,
            title: i18next.t('Start Your Free Website Trial'),
          };
        }

        return {
          ...freeTrialCaptions,
          backButtonLabel: i18next.t('Back'),
        };
      }

      return {
        backButtonLabel: i18next.t('Back'),
        description: {
          first: i18next.t(
            "Choose the website plan that's right for your business. Get visitors, sell products, and grow your business online"
          ),
        },
      };
    }

    if (details.isReactivatingSubscription) {
      return {
        backButtonLabel: i18next.t('Back'),
        title: i18next.t('Reactivate your subscription'),
        description: {
          first: i18next.t('Reactivate subscription to bring your site back online'),
        },
      };
    }

    if (details.isConnectThirdPartyDomain) {
      return {
        backButtonLabel: i18next.t('Back'),
        title: i18next.t('Go premium to connect your domain'),
        description: {
          first: i18next.t(
            'Connecting a custom domain is a Premium feature. Upgrade now to use your own web address and unlock more benefits!'
          ),
        },
      };
    }

    return {
      backButtonLabel: i18next.t('Back'),
      title: i18next.t('Go online with a domain and hosting plan'),
      description: {
        first: i18next.t('Go premium. Choose a custom domain and select a premium plan'),
      },
    };
  }

  if (details.isFreeTrialAvailable) {
    return freeTrialCaptions;
  }

  const targetUpsell = details.forcedUpsell
    ? upsells[details.forcedUpsell]
    : Object.values(upsells).find((upsellDetails) => upsellDetails.premiumFeature === upsellType);

  const upsellDescription =
    targetUpsell?.captions?.paywallDescription || targetUpsell?.captions?.description;

  if (upsellDescription) {
    return {
      backButtonLabel: i18next.t('Back to editor'),
      description: {
        first: upsellDescription,
      },
    };
  }

  return {
    backButtonLabel: i18next.t('Back to editor'),
    ...defaultCaptions,
  };
};

export default getCaptionsForPaywall;
